import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import isEmpty from 'lodash/isEmpty';
import { fetchPlacements } from 'core/actions/ocapi';

import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import InfoBarMarketing from 'core/modules/InfoBarMarketing/InfoBarMarketing';
import InfoBarMarketingTile from 'core/modules/InfoBarMarketing/components/InfoBarMarketingTile';

import { selectActiveMQ, selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import {
  selectInfoBarMarketingIsLoading,
  selectInfoBarMarketingIsLoaded,
  selectInfoBarMarketingResponseDate,
} from 'core/modules/InfoBarMarketing/selectors';

import MerchantLetterboxTile from './components/MerchantLetterboxTile';
import MarketingLetterboxTile from './components/MarketingLetterboxTile';
import LetterboxTOFCarousel from './components/LetterboxTOFCarousel';

import { triggerSlideIsViewed } from './actions';

import {
  selectLetterboxTOFConfig,
  selectMarketingContent,
  selectMerchantContent,
  selectLetterboxTOFIsLoaded,
  selectLetterboxTOFIsLoading,
  selectLetterboxTOFPlacements,
} from './selectors';

import './LetterboxTOF.scss';

function LetterboxTOF({
  letterbox_category_id,
}) {
  const dispatch = useDispatch();

  const activeMQ = useSelector(selectActiveMQ);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const config = useSelector(selectLetterboxTOFConfig);
  const isLoading = useSelector(selectLetterboxTOFIsLoading);
  const isLoaded = useSelector((state) => selectLetterboxTOFIsLoaded(state, letterbox_category_id));
  const placements = useSelector(selectLetterboxTOFPlacements);
  const marketingContent = useSelector(selectMarketingContent);
  const merchantContent = useSelector(selectMerchantContent);
  const infoBarMarketingIsLoading = useSelector(selectInfoBarMarketingIsLoading);
  const infoBarMarketingIsLoaded = useSelector(selectInfoBarMarketingIsLoaded);
  const infoBarMarketingResponseDate = useSelector(selectInfoBarMarketingResponseDate);
  const { showRebateTierName, shouldClientUseExtraPillTileView } = config;

  const shouldUseExtraPillView = shouldClientUseExtraPillTileView && isMobileMQ;

  const [activePlacements, setActivePlacements] = useState(placements);
  const [shouldRenderOnMobile, setShouldRenderOnMobile] = useState(false);

  const slideIsViewedHandler = useCallback((carouselApi) => {
    const currentSlideIndex = carouselApi?.selectedScrollSnap() || 0;
    const slidesTotal = placements.length;
    const currentSlideProps = placements[currentSlideIndex];
    dispatch(triggerSlideIsViewed(currentSlideIndex + 1, slidesTotal, currentSlideProps));
  }, [dispatch, placements]);

  const addTile = () => {
    if (activePlacements.length !== placements.length) {
      setActivePlacements(placements);
    }
  };

  const { tagHelper } = config;

  const handleRef = useCallback((ref) => {
    tagHelper(ref); // handle Chase tagging implementation
  }, [activePlacements]);

  const { api: { params: marketingContentParams, options: marketingContentOptions } } = marketingContent.config;
  const { api: { options: merchantContentOptions } } = merchantContent.config;
  let { api: { params: merchantContentParams } } = merchantContent.config;

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      if (letterbox_category_id) {
        merchantContentParams = {
          ...merchantContentParams,
          letterbox_category_id,
          content_group_id: null,
        };
      }
      dispatch(fetchPlacements(merchantContentParams, merchantContentOptions));
    }
  }, []);

  useEffect(() => {
    if (!isLoaded && !isLoading && !letterbox_category_id) {
      dispatch(fetchPlacements(marketingContentParams, marketingContentOptions));
    }
  }, []);

  useEffect(() => {
    if (isLoaded && !isEmpty(placements)) {
      setActivePlacements(placements);
      slideIsViewedHandler();
    }
  }, [isLoaded, placements, slideIsViewedHandler]);

  useEffect(() => {
    if (isLoaded) {
      setShouldRenderOnMobile(['S', 'XS', 'M'].includes(activeMQ));
    }
  }, [isLoaded, activeMQ]);

  useEffect(() => {
    if (isLoaded && shouldRenderOnMobile) {
      addTile();
    }
  }, [isLoaded, shouldRenderOnMobile]);

  // Fire an action when slide changes
  const removeTile = (id) => {
    const updatedPlacements = activePlacements.filter(offer => offer.id !== id);
    if (updatedPlacements.length !== activePlacements.length) {
      setActivePlacements(updatedPlacements);
    }
  };

  const renderLetterboxItem = ({ placement, ...otherProps }) => {
    if (placement.isInfoBarMarketing && shouldRenderOnMobile) {
      return (
        <InfoBarMarketingTile
          className="mn_letterboxTile mn_infoBarMarketingLetterboxTile"
          responseDate={infoBarMarketingResponseDate}
          removeCallback={() => removeTile(placement.id)}
          {...placement}
          {...otherProps}
        />
      );
    }

    if (placement.isInfoBarMarketing && !shouldRenderOnMobile) {
      removeTile(placement.id);
    }

    return placement.merchant ? (
      <MerchantLetterboxTile
        {...placement}
        {...otherProps}
        config={config}
        shouldUseExtraPillView={shouldUseExtraPillView}
      />
    ) : (
      <MarketingLetterboxTile {...placement} {...otherProps} />
    );
  };

  if (!letterbox_category_id && !infoBarMarketingIsLoading && !infoBarMarketingIsLoaded) {
    // Triggers loading of data without rendering in case letterbox_category_id is not present
    return <InfoBarMarketing preventRender />;
  }

  if (isEmpty(activePlacements)) {
    return null;
  }

  const shouldUseCarousel = activePlacements.length > 1;

  return (
    <div
      className={`mn_letterboxTOF ${shouldUseCarousel ? '' : 'mn_letterboxTOFWithoutCarousel'}`}
      data-test="letterbox-tof"
      ref={handleRef}
    >
      <ContentWrap mobile tablet>
        <LetterboxTOFCarousel
          carousel={config.carousel}
          shouldUseCarousel={shouldUseCarousel}
          onSlideChange={slideIsViewedHandler}
        >
          {activePlacements.map(placement => (
            renderLetterboxItem({ key: placement.id, placement, showRebateTierName })
          ))}
        </LetterboxTOFCarousel>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><LetterboxTOF {...props} /></ComponentErrorBoundary>;
